import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import GradientLineChart from "cxfcomponents/Charts/LineCharts/GradientLineChart";
import {
  fetchWorkflowActivities,
  fetchAnalyticsActivities
} from "redux/actions/portal/flowsActionCreator";
import VerticalBarChart from "cxfcomponents/Charts/BarCharts/VerticalBarChart";
import PieChart from "cxfcomponents/Charts/PieChart";
import { Grid, Icon, Tooltip } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import UserPreferenceDialog from "./components/ImportFlowDialog";
import { getEnv } from "helpers/environment";

const colors = ["primary", "success", "warning", "info", "error", "secondary"];

const successFailRateOptions = {
  responsive: true,
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true
    }
  }
};

const defaultPreferences = {
  WORKFLOW_CONNECTORS_COUNTS_STATUS: getEnv("WORKFLOW_CONNECTORS_COUNTS_STATUS"),
  WORKFLOW_CONNECTORS_EXECUTION_TIME: getEnv("WORKFLOW_CONNECTORS_EXECUTION_TIME"),
  WORKFLOW_CONNECTORS_SUCCESS_FAIL_RATE: getEnv("WORKFLOW_CONNECTORS_SUCCESS_FAIL_RATE"),
  AVERAGE_TIME_TO_CREATE_DEPLOY: getEnv("AVERAGE_TIME_TO_CREATE_DEPLOY"),
  INTEGRATION_USAGE: getEnv("INTEGRATION_USAGE"),
  ERROR_FREQUENCY: getEnv("ERROR_FREQUENCY"),
  WORKFLOW_CONNECTORS_ADOPTION: getEnv("WORKFLOW_CONNECTORS_ADOPTION"),
  THIRD_PARTY_API_PERFORMANCE_VOLUME: getEnv("THIRD_PARTY_API_PERFORMANCE_VOLUME"),
  SYSTEM_LATENCY: getEnv("SYSTEM_LATENCY"),
  WORKFLOW_COMPLEXITY_SCORE: getEnv("WORKFLOW_COMPLEXITY_SCORE"),
  DATA_VOLUME_PER_WORKFLOW_CONNECTOR: getEnv("DATA_VOLUME_PER_WORKFLOW_CONNECTOR"),
  USER_ACTIVITY: getEnv("USER_ACTIVITY"),
  WORKFLOW_BOTTLENECKS: getEnv("WORKFLOW_BOTTLENECKS")
};

const WorkflowActivities = () => {
  const { user } = useAuth0();
  const { workflowActivities, analyticsActivities } = useSelector((store) => store?.portal?.flows);
  const { t } = useTranslation("dashboard");
  const [chartData, setChartData] = useState({});
  const [workflowCountData, setWorkflowCountData] = useState({});
  const [workflowStatusData, setWorkflowStatusData] = useState({});
  const [connectorsCountData, setConnectorsCountData] = useState({});
  const [connectorsStatusData, setConnectorsStatusData] = useState({});
  const [successFailRateBarData, setSuccessFailRateBarData] = useState({});
  const [successFailRateLineData, setSuccessFailRateLineData] = useState({});
  const [openUserPreferenceDialog, setOpenUserPreferenceDialog] = useState(false);
  const { userPreferences } = useSelector((store) => store.portal.users);
  const [modifiedPreferences, setModifiedPreferences] = useState(defaultPreferences);

  const [height, setHeight] = useState("");
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { WORKFLOW_CONNECTORS_COUNTS_STATUS, WORKFLOW_CONNECTORS_SUCCESS_FAIL_RATE } =
    modifiedPreferences;

  const handleOpenUserPreferenceDialog = () => {
    setOpenUserPreferenceDialog(true);
  };

  const handleCloseUserPreferenceDialog = () => {
    setOpenUserPreferenceDialog(false);
  };

  const handleReloadData = () => {
    dispatch(fetchAnalyticsActivities(user?.stripeId));
  };

  useEffect(() => {
    if (workflowActivities && pathname === "/home") {
      const worflowsData = {
        labels: workflowActivities?.months,
        datasets: workflowActivities?.datasets
          ? workflowActivities?.datasets.map((data, index) => ({
              label: data.label,
              color: colors[index],
              data: data.data
            }))
          : []
      };

      setChartData(worflowsData);
    }
    if (analyticsActivities && pathname === "/analytics/") {
      const wFlowCountDatas = {
        labels: [],
        datasets: [
          {
            label: "Count of Workflows",
            color: "success",
            data: []
          }
        ]
      };

      if (analyticsActivities?.flows?.flowsStatusCounts) {
        for (const status in analyticsActivities.flows.flowsStatusCounts) {
          const count = analyticsActivities.flows.flowsStatusCounts[status];
          const capitalizedLabel = status.charAt(0).toUpperCase() + status.slice(1);

          wFlowCountDatas.labels.push(capitalizedLabel);
          wFlowCountDatas.datasets[0].data.push(count);
        }
      }

      setWorkflowCountData(wFlowCountDatas);

      const wFlowStatusDatas = {
        labels: [],
        datasets: {
          label: "Count of Workflows",
          backgroundColors: ["secondary", "success", " warning", "error"],
          data: []
        }
      };

      if (analyticsActivities?.flows?.flowsStatusCounts) {
        for (const status in analyticsActivities.flows.flowsStatusCounts) {
          const count = analyticsActivities.flows.flowsStatusCounts[status];
          const capitalizedLabel = status.charAt(0).toUpperCase() + status.slice(1);
          wFlowStatusDatas.labels.push(capitalizedLabel);
          wFlowStatusDatas.datasets.data.push(count);
        }
      }
      setWorkflowStatusData(wFlowStatusDatas);

      const connectorsCountDatas = {
        labels: [],
        datasets: [
          {
            label: "Count of Connectors",
            color: "success",
            data: []
          }
        ]
      };

      if (analyticsActivities?.connectors) {
        for (const connectorType in analyticsActivities.connectors) {
          const connectorData = analyticsActivities.connectors[connectorType].datasets[0].data;
          const label = analyticsActivities.connectors[connectorType].datasets[0].label;

          connectorsCountDatas.labels.push(label);
          const count = connectorData.reduce((acc, curr) => acc + curr, 0);
          connectorsCountDatas.datasets[0].data.push(count);
        }
      }
      setConnectorsCountData(connectorsCountDatas);

      const connectorsStatusDatas = {
        labels: [],
        datasets: {
          label: "Count of Connectors",
          backgroundColors: ["primary", "secondary", "error"],
          data: []
        }
      };

      if (analyticsActivities?.connectors) {
        for (const connectorType in analyticsActivities.connectors) {
          const connectorData = analyticsActivities.connectors[connectorType].datasets[0].data;
          const label = analyticsActivities.connectors[connectorType].datasets[0].label;

          connectorsStatusDatas.labels.push(label);
          const count = connectorData.reduce((acc, curr) => acc + curr, 0);
          connectorsStatusDatas.datasets.data.push(count);
        }
      }
      setConnectorsStatusData(connectorsStatusDatas);

      const successFailRateDatas = {
        labels: [],
        datasets: [
          {
            label: [],
            data: [],
            color: ["primary"]
          },
          {
            label: [],
            data: [],
            color: ["success"]
          },
          {
            label: [],
            data: [],
            color: ["error"]
          }
        ]
      };

      analyticsActivities?.execution?.labels?.forEach((label) => {
        successFailRateDatas.labels.push(label);
      });

      analyticsActivities?.execution?.datasets?.forEach((dataset, index) => {
        successFailRateDatas.datasets[index].label.push(dataset.label);

        dataset.data.forEach((value) => {
          successFailRateDatas.datasets[index].data.push(value);
        });
      });
      setSuccessFailRateBarData(successFailRateDatas);

      const successFailRateLineDatas = {
        labels: [],
        datasets: [
          {
            label: "Success Executions",
            color: "primary",
            data: []
          },
          {
            label: "Started Executions",
            color: "success",
            data: []
          },
          {
            label: "Failed Executions",
            color: "error",
            data: []
          }
        ]
      };
      analyticsActivities?.execution?.labels?.forEach((label) => {
        successFailRateLineDatas.labels.push(label);
      });

      analyticsActivities?.execution?.datasets?.forEach((dataset, index) => {
        // successFailRateDatas.datasets[index].label.push(dataset.label);

        dataset.data.forEach((value) => {
          successFailRateLineDatas.datasets[index].data.push(value);
        });
      });
      setSuccessFailRateLineData(successFailRateLineDatas);
    }
  }, [workflowActivities, analyticsActivities]);

  useEffect(() => {
    setHeight("350px");
    dispatch(fetchWorkflowActivities(user?.stripeId, user?.cxfUserId));
    dispatch(fetchAnalyticsActivities(user?.stripeId));
  }, []);

  useEffect(() => {
    if (userPreferences instanceof Object && Object.keys(userPreferences).length) {
      const { designer, ...restPreferences } = userPreferences;
      setModifiedPreferences((prev) => ({
        ...prev,
        ...restPreferences
      }));
    }
  }, [userPreferences]);

  return (
    <>
      {pathname === "/home" && (
        <GradientLineChart
          title={t("labels.workflow_activities")}
          description="Monthly flow creation and execution activity"
          height="clamp(220px, 28vh, 310px)"
          chart={chartData}
          fromHome={true}
        />
      )}
      {pathname === "/analytics/" && (
        <>
          <SoftBox mb={3}>
            <SoftBox
              display={{
                xs: "grid",
                lg: "flex"
              }}
              justifyContent={{
                lg: "flex-end"
              }}
              width="100%"
              gap={1}
              sx={{
                gridTemplateColumns: {
                  md: "repeat(2, 1fr)"
                }
              }}
            >
              <Tooltip title="Reload" placement="top">
                <SoftButton
                  variant="gradient"
                  size="medium"
                  color="primary"
                  onClick={handleReloadData}
                >
                  <Icon sx={{ fontSize: "500px !important" }}>autorenew</Icon>
                </SoftButton>
              </Tooltip>
              <Tooltip title="User Preference" placement="top">
                <SoftButton
                  variant="outlined"
                  size="medium"
                  color="primary"
                  onClick={handleOpenUserPreferenceDialog}
                >
                  <Icon sx={{ fontSize: "500px !important" }}>settings</Icon>
                </SoftButton>
              </Tooltip>
            </SoftBox>
          </SoftBox>
          <SoftBox mb={3} display={WORKFLOW_CONNECTORS_COUNTS_STATUS === "hidden" ? "none" : ""}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <VerticalBarChart
                  title={t("labels.workflow_counts")}
                  height={height}
                  chart={workflowCountData}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <PieChart
                  title={t("labels.workflow_status")}
                  height={height}
                  chart={workflowStatusData}
                />
              </Grid>
            </Grid>
          </SoftBox>
          <SoftBox mb={3} display={WORKFLOW_CONNECTORS_COUNTS_STATUS === "hidden" ? "none" : ""}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <VerticalBarChart
                  title={t("labels.connectors_counts")}
                  height={height}
                  chart={connectorsCountData}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <PieChart
                  title={t("labels.connectors_status")}
                  height={height}
                  chart={connectorsStatusData}
                />
              </Grid>
            </Grid>
          </SoftBox>
          <SoftBox
            mb={3}
            display={WORKFLOW_CONNECTORS_SUCCESS_FAIL_RATE === "hidden" ? "none" : ""}
          >
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <VerticalBarChart
                  title={t("labels.workflow_connectors_success_fail_rate")}
                  height={height}
                  options={successFailRateOptions}
                  chart={successFailRateBarData}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <GradientLineChart
                  title={t("labels.workflow_connectors_success_fail_rate")}
                  height={height}
                  chart={successFailRateLineData}
                />
              </Grid>
            </Grid>
          </SoftBox>
        </>
      )}
      <UserPreferenceDialog
        open={openUserPreferenceDialog}
        onClose={handleCloseUserPreferenceDialog}
        dialogTitle={"User Preference"}
        acceptButtonText={"Confirm"}
        acceptButtonColor={"info"}
        userPreferences={modifiedPreferences}
      />
    </>
  );
};

export default WorkflowActivities;
